@if (notFound()) {
  <div class="deal-empty">
    <i class="pi pi-inbox" aria-hidden="true"></i>
    <strong>Deal não encontrado</strong>
    <span>
      Ele pode ter sido removido, ou pertencer a outro parceiro. Volte para a lista e tente de novo.
    </span>
    <a routerLink="/dashboard/commercial/my-deals" class="deal-empty__link"
      >Voltar para Meus deals</a
    >
  </div>
} @else if (deal(); as d) {
  <div class="deal" [class.deal--fill-tab]="shownTab === 'briefing'">
    <!-- O componente do design system, o mesmo de `my-deals`, `register-deal` e
         `partner-users`. -->
    <app-breadcrumb [home]="home" [items]="breadcrumbs" />

    <!-- Tempo real (card 3496): quem mais está com o deal aberto e aviso de mudança. O parceiro
         só relê (o GET já projeta a etapa); o time interno vê o aviso com Recarregar. -->
    <app-realtime-presence-banner
      [topic]="partnerDealRealtime.topic(d.id)"
      [texts]="partnerDealRealtime.texts"
      [changeEventTypes]="partnerDealRealtime.changeEventTypes"
      [ignoredChanges]="partnerDealRealtime.ignoredChanges"
      [changeMode]="visaoInterna ? 'notice' : 'silent-reload'"
      (reloadRequested)="reloadFromRealtime(false)"
      (silentRefreshRequested)="reloadFromRealtime(true)" />

    <!-- ═══════════════════════ cartão de identidade ═══════════════════════ -->
    <!-- Identidade e jornada no MESMO cartão, como no design: são a resposta a "que deal é
         este e onde ele está", e separá-los em dois cartões faria a tela começar com duas
         caixas dizendo metade de uma coisa só. -->
    <section class="hero">
      <div class="hero__main">
        <!-- O canto recortado (15/4/15/15) é a assinatura da marca, o "L" do logo. -->
        <span class="hero__badge" aria-hidden="true">
          <i class="pi pi-building"></i>
        </span>

        <div class="hero__identity">
          <div class="hero__title-row">
            <h1 class="hero__title">{{ d.corporateName }}</h1>

            @if (term(); as t) {
              <span class="pill" [class]="'pill--' + termStatusSeverity[t.status]">
                <span class="pill__dot" aria-hidden="true"></span>
                {{ termStatusLabels[t.status] }}
              </span>
            }

            @if (d.rejectedAt) {
              <span class="pill pill--danger">
                <span class="pill__dot" aria-hidden="true"></span>
                Rejeitado
              </span>
            }
          </div>

          <div class="hero__meta">
            <span class="hero__fact">
              <span class="hero__fact-label">CNPJ</span>
              <span class="hero__fact-value hero__fact-value--num">
                {{ d.cnpj | vaultMaskCnpj }}
              </span>
              <!-- O CNPJ é o que se cola numa consulta externa, e reler dígito a dígito é
                   onde o erro entra. -->
              <button
                type="button"
                class="hero__copy"
                [title]="'Copiar CNPJ'"
                aria-label="Copiar CNPJ"
                (click)="copiarCnpj()">
                <i [class]="cnpjCopiado() ? 'pi pi-check' : 'pi pi-copy'" aria-hidden="true"></i>
              </button>
            </span>

            <span class="hero__divider" aria-hidden="true"></span>

            <span class="hero__fact">
              <span class="hero__fact-label">Valor</span>
              <span class="hero__fact-value hero__fact-value--num hero__fact-value--strong">
                {{ d.currencyCode }} {{ d.amount | number: '1.0-0' }}
              </span>
            </span>

            <span class="hero__divider" aria-hidden="true"></span>

            <!-- Prazo da captação e flat fee estimado (card 3683). Deal antigo, sem prazo: "—". -->
            @let dealTermSummary = dealTerm;
            <span class="hero__fact">
              <span class="hero__fact-label">Prazo</span>
              <span class="hero__fact-value hero__fact-value--num">{{
                dealTermSummary.label
              }}</span>
              @if (dealTermSummary.estimate; as estimate) {
                <span
                  class="hero__fact-note"
                  title="Flat fee estimado pela tabela da Calculadora do Parceiro">
                  flat fee {{ estimate.flatFeePercent | vaultFormatPercent: 2 }} ≈
                  {{ d.currencyCode }} {{ estimate.flatFeeAmount | number: '1.0-0' }}
                </span>
              }
            </span>

            <span class="hero__divider" aria-hidden="true"></span>

            <!-- Na visão da Legatus vem o NOME do parceiro: com vários parceiros no board,
                 saber que veio de um parceiro não diz nada — saber de qual, sim. -->
            <span class="hero__fact hero__fact--plain">
              <i class="pi pi-user" aria-hidden="true"></i>
              {{ origemDoDeal }}
            </span>

            @if (diasDeReserva !== null) {
              <span class="hero__divider" aria-hidden="true"></span>
              <span class="hero__fact hero__fact--plain" title="Prazo de exclusividade do CNPJ">
                <i class="pi pi-lock" aria-hidden="true"></i>
                Reserva: {{ diasDeReserva }}d restantes
              </span>
            }
          </div>
        </div>

        <div class="hero__actions">
          <!-- Jornada do deal | Jornada nos bancos (card 3752). A visão dos bancos é a aba `banks` da URL; sem
               operação ligada ao deal o lado dos bancos fica travado, com cadeado. -->
          <app-deal-journey-switch
            [banksActive]="shownTab === 'banks'"
            (journeyChange)="selectTab($event === 'banks' ? 'banks' : 'overview')" />

          <app-button
            [label]="'Histórico'"
            [icon]="'history'"
            [theme]="'outline-grey'"
            [type]="'button'"
            (sendButtonClick)="selectTab('timeline')"></app-button>

          <!-- Decisão da Legatus (bloco A). Só aparece para o time interno, e quem diz isso é o
               servidor (`canDecide`) — a tela não reimplementa a régua. -->
          @if (podePreAprovar) {
            <app-button
              [label]="'Pré-aprovar'"
              [icon]="'check'"
              [isLoading]="acaoEmCurso() === 'pre-aprovar'"
              [loadingText]="'Pré-aprovando...'"
              [disabled]="acaoEmCurso() !== null"
              [type]="'button'"
              (sendButtonClick)="preAprovar()"></app-button>
          }

          @if (podeRejeitar) {
            <app-button
              [label]="'Rejeitar'"
              [icon]="'times'"
              [theme]="'danger'"
              [disabled]="acaoEmCurso() !== null"
              [type]="'button'"
              (sendButtonClick)="abrirDecisao('rejeitar')"></app-button>
          }

          @if (podeReabrir) {
            <app-button
              [label]="'Reabrir deal'"
              [icon]="'replay'"
              [disabled]="acaoEmCurso() !== null"
              [type]="'button'"
              (sendButtonClick)="abrirDecisao('reabrir')"></app-button>
          }
        </div>
      </div>

      <!-- ─────────────────────── jornada do deal ─────────────────────── -->
      <!-- Card 3752: na visão Jornada nos bancos, o resumo por etapa do banco ocupa o lugar do stepper do deal. -->
      <div class="journey">
        @if (shownTab === 'banks') {
          <app-deal-bank-journey-summary />
        } @else {
          <div class="journey__head">
            <span class="eyebrow">Jornada do deal</span>
            <span class="journey__position">
              Etapa <strong>{{ posicaoNoFunil.atual }}</strong> de {{ posicaoNoFunil.total }}
            </span>
          </div>

          <ol class="journey__steps" aria-label="Etapas do deal">
            @for (passo of stepper; track passo.stage; let i = $index, last = $last) {
              <li
                class="journey__step"
                [class.journey__step--done]="passo.done"
                [class.journey__step--skipped]="passo.skipped"
                [class.journey__step--current]="passo.current"
                [class.journey__step--selectable]="canSelectStage(passo)"
                [class.journey__step--selected]="passo.stage === shownStage && !passo.current"
                [attr.aria-current]="passo.current ? 'step' : null"
                [attr.role]="canSelectStage(passo) ? 'button' : null"
                [attr.tabindex]="canSelectStage(passo) ? 0 : null"
                [attr.title]="
                  passo.skipped
                    ? 'Etapa pulada ao mover o deal: continua pendente'
                    : canSelectStage(passo)
                      ? 'Ver o conteúdo desta etapa'
                      : null
                "
                (click)="selectStage(passo)"
                (keydown.enter)="selectStage(passo)"
                (keydown.space)="$event.preventDefault(); selectStage(passo)">
                <div class="journey__track">
                  <span class="journey__marker">
                    @if (passo.done) {
                      <i class="pi pi-check" aria-hidden="true"></i>
                    } @else if (passo.skipped) {
                      <!-- Pulada no arrasto (card 3189): pendência, não conclusão. -->
                      <i class="pi pi-clock" aria-label="Pendente"></i>
                    } @else {
                      {{ i + 1 }}
                    }
                  </span>
                  <!-- A linha liga esta etapa à seguinte; a última não tem a quem ligar. -->
                  @if (!last) {
                    <span class="journey__line" aria-hidden="true"></span>
                  }
                </div>

                <div class="journey__texts">
                  <span class="journey__label">{{ passo.label }}</span>
                  @if (legendaDaEtapa(passo); as legenda) {
                    <span class="journey__caption">
                      @if (legenda.data) {
                        {{ legenda.data | vaultDate }}
                      } @else {
                        {{ legenda.texto }}
                      }
                    </span>
                  }
                </div>
              </li>
            }
          </ol>
        }
      </div>
    </section>

    <!-- A faixa fica acima das abas de propósito: ela é o único aviso que continua visível na
         Linha do tempo. O motivo vem aqui em uma linha; a observação por extenso e a data ficam
         no cartão da lateral, para a faixa não virar um parágrafo. -->
    @if (d.rejectedAt) {
      <div class="callout callout--danger" role="status">
        <i class="pi pi-times-circle" aria-hidden="true"></i>
        <span>
          <strong
            >Deal rejeitado{{ rejeicaoAtual?.motivo ? ': ' + rejeicaoAtual?.motivo : '' }}.</strong
          >
          A etapa em que ele parou foi preservada.
        </span>
      </div>
    }

    <!-- ───────────────────────────── abas ───────────────────────────── -->
    <!-- Card 3752: a aba Bancos saiu daqui e virou a visão Jornada nos bancos do cabeçalho. Nessa visão as abas do
         deal somem; a aba dos bancos traz as próprias (Quadro e Lista). -->
    @if (shownTab !== 'banks') {
      <div class="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="tabs__tab"
          [class.tabs__tab--active]="shownTab === 'overview'"
          [attr.aria-selected]="shownTab === 'overview'"
          (click)="selectTab('overview')">
          <i class="pi pi-th-large" aria-hidden="true"></i>
          Visão geral
        </button>

        <!-- Reuniões (card 3112; era "Ligações" até o card 3683, o id da aba continua `calls`): time
           interno e parceiro do deal (a empresa não chega a esta tela). -->
        <button
          type="button"
          role="tab"
          class="tabs__tab"
          [class.tabs__tab--active]="shownTab === 'calls'"
          [attr.aria-selected]="shownTab === 'calls'"
          (click)="selectTab('calls')">
          <i class="pi pi-video" aria-hidden="true"></i>
          Reuniões
        </button>

        <!-- Documentos em todas as etapas do deal (card 3239): o onboarding embutido é o mesmo
           componente da Documentação, do Registro à Operação. -->
        <button
          type="button"
          role="tab"
          class="tabs__tab"
          [class.tabs__tab--active]="shownTab === 'documents'"
          [attr.aria-selected]="shownTab === 'documents'"
          (click)="selectTab('documents')">
          <i class="pi pi-folder-open" aria-hidden="true"></i>
          Documentos
        </button>

        <!-- Análise econômica: em todas as etapas, para interno e parceiro, quando a empresa tem dados
           (card 3307; ver `canSeeEconomicAnalysis` em deal-tabs.ts). -->
        @if (canSeeEconomicAnalysis) {
          <button
            type="button"
            role="tab"
            class="tabs__tab"
            [class.tabs__tab--active]="shownTab === 'economic-analysis'"
            [attr.aria-selected]="shownTab === 'economic-analysis'"
            (click)="selectTab('economic-analysis')">
            <i class="pi pi-chart-line" aria-hidden="true"></i>
            Análise econômica
          </button>
        }

        <!-- KYC (card 3380): em todas as etapas, só para o time interno e só quando há dado (o cadastro
           da Receita ou o termo do cliente assinado). Ver `canSeeKyc` em deal-tabs.ts. -->
        @if (canSeeKyc) {
          <button
            type="button"
            role="tab"
            class="tabs__tab"
            [class.tabs__tab--active]="shownTab === 'kyc'"
            [attr.aria-selected]="shownTab === 'kyc'"
            (click)="selectTab('kyc')">
            <i class="pi pi-id-card" aria-hidden="true"></i>
            KYC
          </button>
        }

        <button
          type="button"
          role="tab"
          class="tabs__tab"
          [class.tabs__tab--active]="shownTab === 'timeline'"
          [attr.aria-selected]="shownTab === 'timeline'"
          (click)="selectTab('timeline')">
          <i class="pi pi-history" aria-hidden="true"></i>
          Linha do tempo
          @if (timeline()?.entries?.length) {
            <span class="tabs__count">{{ timeline()!.entries.length }}</span>
          }
        </button>

        <!-- Cotação (card 3611): em todas as etapas, para interno e parceiro; a última aba (Marcio, 21/10/2026). Empréstimos com status
           Cotado da empresa do deal, pela rota do deal (`partners/deals/{id}/quoted-loans`). -->
        <button
          type="button"
          role="tab"
          class="tabs__tab"
          [class.tabs__tab--active]="shownTab === 'quotes'"
          [attr.aria-selected]="shownTab === 'quotes'"
          (click)="selectTab('quotes')">
          <i class="pi pi-money-bill" aria-hidden="true"></i>
          Cotação
        </button>

        <!-- Briefing Comercial (card 3878): em todas as etapas, só para o time interno; a última aba. Ver
           `canSeeBriefing` em deal-tabs.ts. O parceiro que abre `?tab=briefing` cai na Visão geral. -->
        @if (canSeeBriefing) {
          <button
            type="button"
            role="tab"
            class="tabs__tab"
            [class.tabs__tab--active]="shownTab === 'briefing'"
            [attr.aria-selected]="shownTab === 'briefing'"
            (click)="selectTab('briefing')">
            <i class="pi pi-file-edit" aria-hidden="true"></i>
            Briefing
          </button>
        }
      </div>
    }

    <!-- ─────────────────────────── briefing ─────────────────────────── -->
    <!-- Componente próprio (card 3878): carga, erro, geração, versões, PDF e o chat do Cog IA são dele. -->
    @if (shownTab === 'briefing') {
      <app-deal-briefing-tab
        [dealId]="d.id"
        [dealSequentialId]="d.sequentialId"
        [companyId]="d.companyId" />
    }

    <!-- ─────────────────────────── bancos ─────────────────────────── -->
    <!-- Card 3707: o quadro da Jornada nos bancos, só com os bancos deste deal (dado do `DealBankJourneyStore`). -->
    @if (shownTab === 'banks') {
      <app-deal-bank-journey-tab />
    }

    <!-- ─────────────────────────── cotação ─────────────────────────── -->
    <!-- Painel próprio (card 3611): carga, erro e vazio são dele. O banco abre a modal do empréstimo; o parceiro, em só leitura. -->
    @if (shownTab === 'quotes') {
      <app-deal-quotes-panel [dealId]="d.id" [readOnlyLoan]="!visaoInterna" />
    }

    <!-- ─────────────────────────── ligações ─────────────────────────── -->
    <!-- Painel próprio (card 3112): estado, carga e erro são dele — falha ali não derruba a tela.
         Desvincular só para o time interno (`visaoInterna`, do `canDecide` do servidor). -->
    @if (shownTab === 'calls') {
      <app-deal-meetings-panel [dealId]="d.id" [podeDesvincular]="visaoInterna" />
    }

    <!-- ─────────────────────────── documentos ─────────────────────────── -->
    <!-- O Data Room da empresa, com a _TRIAGEM, é o repositório (card 3281, T04). O checklist do onboarding
         (cards 3030/3239) saiu da aba no card 3797. -->
    @if (shownTab === 'documents') {
      <section class="deal-documents" role="tabpanel">
        <app-deal-data-room-tab
          [dealId]="d.id"
          [companyId]="d.companyId"
          [visaoInterna]="visaoInterna"
          (bankKitRequested)="reloadTimeline()" />
      </section>
    }

    <!-- ─────────────────────── análise econômica ─────────────────────── -->
    <!-- A própria DFP Histórica (rota da Análise Econômica), embutida com a empresa do deal: mesmas
         tabelas e endpoints, sem seletor de empresa, sem tour e sem gravar ?companyId na URL. -->
    @if (shownTab === 'economic-analysis') {
      <section class="deal-economic-analysis" role="tabpanel">
        <!-- Interno: DFP Histórica sem escopo (card 3298). Parceiro: só leitura, pelo endpoint do deal (card 3307). -->
        @if (visaoInterna) {
          <app-deal-economic-analysis-tab [companyId]="d.companyId" />
        } @else {
          <app-deal-partner-economic-analysis-tab [dealId]="d.id" [companyId]="d.companyId" />
        }
      </section>
    }

    <!-- ──────────────────────────────── KYC ──────────────────────────────── -->
    <!-- Documentos assinados, cadastro da Receita e quadro societário (card 3380). -->
    @if (shownTab === 'kyc') {
      <section class="deal-kyc">
        <app-deal-kyc-tab
          [company]="kycCompany()"
          [term]="term()"
          [openingTerm]="acaoEmCurso() === 'ver-termo'"
          [dealId]="deal()?.id ?? null"
          [companyName]="deal()?.corporateName ?? null"
          [canInviteClient]="canInviteClient()"
          [dealRejected]="!!deal()?.rejectedAt"
          (openTerm)="verTermo()"
          (clientInvited)="reloadTimeline()" />
      </section>
    }

    <!-- ─────────────────────────── visão geral ─────────────────────────── -->
    @if (shownTab === 'overview') {
      <div class="grid" role="tabpanel">
        <!--
          O conteúdo principal é o da etapa EXIBIDA (a atual, ou a que a pessoa escolheu rever no
          stepper): Registro mostra quem é a empresa na Receita (card 2986); Consentimento, o termo.
        -->
        @switch (shownStage) {
          @case ('Registro') {
            <div class="grid__main grid__main--bare">
              <app-deal-registry-panel [cnpj]="d.cnpj" />
            </div>
          }
          @case ('Consentimento') {
            <!-- ══════════════ consentimento do cliente ══════════════ -->
            <section class="card grid__main">
              <div class="card__head">
                <span class="card__icon" aria-hidden="true">
                  <i class="pi pi-file-edit"></i>
                </span>
                <div class="card__titles">
                  <h2 class="card__title">
                    {{ visaoInterna ? 'Consentimento do cliente' : 'NDA e Bacen' }}
                  </h2>
                  <p class="card__subtitle">
                    NDA + carta de autorização de SCR
                    @if (term(); as t) {
                      · versão {{ t.termsVersion }}
                    }
                  </p>
                </div>

                <div class="card__actions">
                  <!-- "Ver termo", e não "Baixar": o PDF abre no visualizador compartilhado, na
                   própria tela. Só aparece quando existe termo — sem termo não há o que ver. -->
                  @if (term()) {
                    <button
                      type="button"
                      class="linkish"
                      [disabled]="acaoEmCurso() !== null"
                      [title]="'Ver o documento sem sair da tela'"
                      (click)="verTermo()">
                      <i class="pi pi-file-pdf" aria-hidden="true"></i>
                      Ver termo
                    </button>
                  }

                  <!-- As DUAS opções convivem: aqui a cobrança de todos de uma vez, e na linha de
                   cada signatário a dele. -->
                  @if (termoEmAndamento && pendentesDeAssinatura > 0) {
                    <button
                      type="button"
                      class="linkish"
                      [disabled]="acaoEmCurso() !== null"
                      (click)="reenviarParaTodos()">
                      <i class="pi pi-send" aria-hidden="true"></i>
                      Enviar para todos
                    </button>
                  }

                  <!-- Termo assinado não aceita mais alteração — o backend recusa, e oferecer o
                   botão seria prometer o que não acontece. -->
                  @if (term()?.status !== 'Assinado') {
                    <button type="button" class="linkish" (click)="abrirModalDeAssinantes()">
                      <i class="pi pi-plus" aria-hidden="true"></i>
                      {{ termoEmAndamento ? 'Acrescentar signatário' : 'Solicitar assinatura' }}
                    </button>
                  }
                </div>
              </div>

              @if (term(); as t) {
                <!--
              O termo é UM POR CLIENTE. Quando ele nasceu antes deste deal, mexer nos assinantes
              por aqui muda o que o outro deal mostra — e quem está na tela precisa saber disso
              ANTES de mexer, não depois.
            -->
                @if (t.reusedFromAnotherDeal) {
                  <p class="note note--warn">
                    <i class="pi pi-info-circle" aria-hidden="true"></i>
                    <span>
                      Este termo foi aberto em outro deal desta mesma empresa. Alterações aqui valem
                      para os dois.
                    </span>
                  </p>
                }

                <!-- ──────────── faixa de progresso ──────────── -->
                <div class="strip">
                  <div class="strip__progress">
                    <div class="strip__progress-head">
                      <span class="eyebrow">Assinaturas</span>
                      <span class="strip__count">
                        {{ progressoDeAssinaturas.assinaram }} de {{ progressoDeAssinaturas.total }}
                      </span>
                    </div>
                    <div
                      class="strip__bar"
                      role="progressbar"
                      [attr.aria-valuenow]="progressoPercentual"
                      aria-valuemin="0"
                      aria-valuemax="100">
                      <span class="strip__fill" [style.width.%]="progressoPercentual"></span>
                    </div>
                  </div>

                  <div class="strip__facts">
                    <div class="strip__fact">
                      <span class="eyebrow">Enviado em</span>
                      <span class="strip__value">
                        {{ t.requestedAt ? (t.requestedAt | vaultDateTime) : '—' }}
                      </span>
                    </div>
                    <div class="strip__fact">
                      <span class="eyebrow">Concluído em</span>
                      <!--
                    É a data da ÚLTIMA assinatura, e não a do processamento: é ela que tem valor
                    probatório, e a diferença entre as duas pode ser de horas.
                  -->
                      <span class="strip__value">
                        {{ t.signedAt ? (t.signedAt | vaultDateTime) : '—' }}
                      </span>
                    </div>
                    <div class="strip__fact">
                      <span class="eyebrow">Duração</span>
                      <span class="strip__value">{{ duracaoDoTermo }}</span>
                    </div>
                  </div>
                </div>

                <!--
              O termo existe e tem assinantes, mas o envio ao provedor não completou. Sem dizer
              isso a tela pareceria "aguardando" — e a pessoa ficaria cobrando um convite que
              nunca saiu.
            -->
                @if (!termoEmAndamento && progressoDeAssinaturas.total) {
                  <p class="note note--alert">
                    <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                    <span>
                      O envio para assinatura <strong>não foi concluído</strong> e ninguém recebeu o
                      convite. Use <strong>Solicitar assinatura</strong> para tentar de novo —
                      confira antes se todos os nomes têm sobrenome.
                    </span>
                  </p>
                }

                <!-- ──────────── quem assina ──────────── -->
                <div class="signers">
                  @for (lado of ['Cliente', 'Legatus']; track lado) {
                    @if (assinantesDoLado($any(lado)).length) {
                      <div class="signers__group">
                        <div class="signers__group-head">
                          <span class="eyebrow">
                            {{ lado === 'Cliente' ? 'Pelo cliente' : 'Pela Legatus' }}
                          </span>
                          <span class="signers__rule" aria-hidden="true"></span>
                        </div>

                        @for (s of assinantesDoLado($any(lado)); track s.id) {
                          <div class="signer" [class.signer--signed]="s.signedAt">
                            <span class="signer__avatar" aria-hidden="true">
                              {{ iniciais(s.name) }}
                            </span>

                            <div class="signer__who">
                              <div class="signer__name-row">
                                <span class="signer__name">{{ s.name }}</span>
                                <span class="tag">{{ termOriginLabels[s.origin] }}</span>
                              </div>
                              <span class="signer__email">{{ s.email }}</span>
                            </div>

                            <div class="signer__state">
                              @if (s.signedAt) {
                                <span class="signer__check" aria-hidden="true">
                                  <i class="pi pi-check"></i>
                                </span>
                                <span class="signer__state-texts">
                                  <span class="signer__state-title">Assinado</span>
                                  <span class="signer__state-when">
                                    {{ s.signedAt | vaultDateTime }}
                                  </span>
                                </span>
                              } @else if (termoEmAndamento) {
                                <span class="pill pill--warn">
                                  <span class="pill__dot" aria-hidden="true"></span>
                                  Pendente
                                </span>
                                <!-- Cobrar só faz sentido para quem não assinou. -->
                                <button
                                  type="button"
                                  class="linkish"
                                  [disabled]="acaoEmCurso() !== null"
                                  [title]="'Reenviar o convite por e-mail'"
                                  (click)="reenviarCobranca(s)">
                                  <i class="pi pi-bell" aria-hidden="true"></i>
                                  Lembrar
                                </button>
                              } @else {
                                <!-- Termo que não chegou ao provedor: não há convite do outro lado,
                                 então "Pendente" mentiria e Lembrar só poderia falhar. -->
                                <span class="pill pill--muted">Não enviado</span>
                              }

                              <!-- `canBeRemoved` vem CALCULADO do servidor: quem já assinou não sai,
                               o último do cliente não sai, e o lado Legatus vem da
                               Parametrização. Recalcular aqui duplicaria a régua. -->
                              @if (s.canBeRemoved) {
                                <button
                                  type="button"
                                  class="linkish linkish--danger"
                                  [disabled]="acaoEmCurso() !== null"
                                  [title]="'Remover este assinante'"
                                  aria-label="Remover este assinante"
                                  (click)="removerAssinante(s)">
                                  <i class="pi pi-trash" aria-hidden="true"></i>
                                </button>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    }
                  }
                </div>
              } @else {
                <!--
              Termo ausente é situação NORMAL, não erro: o deal só chega aqui depois da
              pré-aprovação, e a assinatura é pedida a partir deste ponto.
            -->
                <p class="note">
                  O NDA e a carta de autorização de SCR ainda não foram enviados para assinatura.
                </p>
              }
            </section>
          }
          @case ('Gates') {
            <!-- Gates de elegibilidade (card 3281, T05): números da DFP extraída x limites da
                 Parametrização. Gates é etapa interna (card 3088): só o time interno chega aqui. -->
            <div class="grid__main grid__main--bare">
              <app-deal-gates-panel [dealId]="d.id" />
            </div>
          }
          @case ('Comite') {
            <!-- Comitê de crédito (card 3789): votação, discussão imutável e envio. Etapa interna:
                 o parceiro nunca chega aqui, e o servidor recusa quem tem parceiro. -->
            @if (visaoInterna && canViewCommittee()) {
              <div class="grid__main grid__main--bare">
                <app-deal-committee-panel
                  [dealId]="d.id"
                  [companyName]="d.corporateName"
                  [reloadKey]="committeeReloadKey()"
                  (viewChange)="committeeView.set($event)"
                  (dealChanged)="onCommitteeDealChanged(null, true)" />
              </div>
            }
          }
          @default {
            <!-- Etapa cujo conteúdo é card próprio. Anuncia O QUE vai morar aqui em vez de deixar
                 a coluna vazia: vazio se lê como defeito, e um texto que nomeia o que vem se lê
                 como roadmap. -->
            @if (conteudoDaEtapa; as conteudo) {
              <section class="card grid__main">
                <div class="card__head">
                  <span class="card__icon" aria-hidden="true">
                    <i class="pi pi-clock"></i>
                  </span>
                  <div class="card__titles">
                    <h2 class="card__title">{{ conteudo.titulo }}</h2>
                    <p class="card__subtitle">Etapa {{ rotuloEtapa[shownStage!] }}</p>
                  </div>
                </div>

                <p class="note">{{ conteudo.texto }}</p>

                <p class="note">
                  Esta etapa ainda não tem tela própria. Enquanto isso, a
                  <strong>Linha do tempo</strong> mostra por onde o deal passou e há quanto tempo
                  ele está aqui — e o cadastro da empresa continua em <strong>Registro</strong>.
                </p>
              </section>
            }
          }
        }

        <!-- ══════════════════════ coluna lateral ══════════════════════ -->
        <aside class="grid__aside">
          <!-- O cartão navy é o único elemento escuro da tela, de propósito: ele responde
               "e agora?", que é a pergunta com que a pessoa chega aqui. -->
          @if (proximoPasso; as passo) {
            <section
              class="next"
              [class.next--waiting]="passo.tom === 'aguardando'"
              [class.next--rejected]="passo.tom === 'rejeitado'">
              <span class="next__eyebrow">{{ passo.eyebrow }}</span>
              <div class="next__texts">
                <h3 class="next__title">{{ passo.titulo }}</h3>
                <p class="next__text">{{ passo.texto }}</p>
                @if (passo.detalhe) {
                  <span class="next__detail">{{ passo.detalhe }}</span>
                }
              </div>

              @switch (passo.acao) {
                @case ('preaprovar') {
                  <app-button
                    [label]="'Pré-aprovar'"
                    [icon]="'check'"
                    [isLoading]="acaoEmCurso() === 'pre-aprovar'"
                    [loadingText]="'Pré-aprovando...'"
                    [disabled]="acaoEmCurso() !== null"
                    [type]="'button'"
                    (sendButtonClick)="preAprovar()"></app-button>
                }
                @case ('reabrir') {
                  <app-button
                    [label]="'Reabrir deal'"
                    [icon]="'replay'"
                    [disabled]="acaoEmCurso() !== null"
                    [type]="'button'"
                    (sendButtonClick)="abrirDecisao('reabrir')"></app-button>
                }
                @case ('avancar') {
                  <app-button
                    [label]="'Avançar etapa'"
                    [icon]="'arrow-right'"
                    [iconPosition]="'right'"
                    [isLoading]="acaoEmCurso() === 'avancar'"
                    [loadingText]="'Avançando...'"
                    [disabled]="acaoEmCurso() !== null"
                    [type]="'button'"
                    (sendButtonClick)="avancarEtapa()"></app-button>
                  @if (passo.secondaryAction === 'submitToCommittee') {
                    <!-- Comitê sem rodada em votação (card 3789): avançar à mão continua possível. -->
                    <app-button
                      [label]="'Enviar ao comitê'"
                      [icon]="'send'"
                      [theme]="'white'"
                      [disabled]="acaoEmCurso() !== null"
                      [type]="'button'"
                      (sendButtonClick)="openCommitteeSubmit()"></app-button>
                  }
                }
                @case ('solicitar') {
                  <app-button
                    [label]="'Solicitar assinatura'"
                    [icon]="'send'"
                    [type]="'button'"
                    (sendButtonClick)="abrirModalDeAssinantes()"></app-button>
                }
                @case ('submitToCommittee') {
                  <app-button
                    [label]="'Enviar ao comitê'"
                    [icon]="'send'"
                    [disabled]="acaoEmCurso() !== null"
                    [type]="'button'"
                    (sendButtonClick)="openCommitteeSubmit()"></app-button>
                }
                @case ('cobrar') {
                  <app-button
                    [label]="'Reenviar termo'"
                    [icon]="'send'"
                    [disabled]="acaoEmCurso() !== null"
                    [type]="'button'"
                    (sendButtonClick)="reenviarParaTodos()"></app-button>
                }
              }
            </section>
          }

          <!-- Comitê de crédito (card 3789): prazo, regras e material, só na etapa Comitê exibida. -->
          @if (visaoInterna && canViewCommittee() && shownStage === 'Comite') {
            <app-deal-committee-aside [view]="committeeView()" (openTab)="selectTab($event)" />
          }

          <!-- Bancos da operação (card 3707): os bancos do deal em lista curta, com a mesma regra da aba Bancos. -->
          @if (canSeeBanks) {
            <app-deal-bank-journey-card (viewAll)="selectTab('banks')" />
          }

          <!-- Resumo cadastral (card 3380): só o time interno, e só quando a consulta VAAS (card 3075) trouxer dado. -->
          @if (visaoInterna) {
            <app-deal-registry-summary [summary]="registrySummary()" />
          }

          <!-- Consentimento SCR (cards 3380 e 3418): o mesmo card da aba KYC; só quando o termo existe,
               e o parceiro também vê. -->
          @if (consentNotice) {
            <app-deal-consent-card
              [term]="term()"
              [openingTerm]="acaoEmCurso() === 'ver-termo'"
              (openTerm)="verTermo()" />
          }

          <!-- Acesso do cliente (card 3461): o parceiro vê aqui; o time interno, na aba KYC, ou aqui
               quando a aba KYC ainda não aparece (sem cadastro da Receita nem termo assinado). -->
          @if ((!visaoInterna || !canSeeKyc) && canInviteClient()) {
            <app-client-access-card
              [dealId]="d.id"
              [companyName]="d.corporateName"
              [rejected]="!!d.rejectedAt"
              (invited)="reloadTimeline()" />
          }

          <!-- Gates · valores internos (card 3380): só o time interno, e só quando já há número. -->
          @if (visaoInterna) {
            <app-deal-gates-summary [dealId]="d.id" />
          }

          <section class="card card--tight">
            <h3 class="card__title card__title--sm">Situação do deal</h3>
            <dl class="rows">
              <div class="rows__row">
                <dt><i class="pi pi-flag" aria-hidden="true"></i>Etapa atual</dt>
                <dd>{{ rotuloEtapa[d.stage] }}</dd>
              </div>
              <div class="rows__row">
                <dt><i class="pi pi-clock" aria-hidden="true"></i>Nesta etapa há</dt>
                <dd>{{ formatarDuracao(timeline()?.timeInCurrentStage) }}</dd>
              </div>
              <div class="rows__row">
                <dt><i class="pi pi-calendar" aria-hidden="true"></i>Registrado em</dt>
                <dd class="rows__value--num">{{ d.createdAt | vaultDateTime }}</dd>
              </div>
              <div class="rows__row">
                <dt><i class="pi pi-user" aria-hidden="true"></i>Origem</dt>
                <!-- Deal da Legatus também grava Origin = Parceiro (o enum só distingue o vínculo
                     manual); quem diz que é da Legatus é o partnerId nulo, como no selo do board. -->
                <dd>
                  {{ d.origin === 'Manual' ? 'Deal manual' : d.partnerId ? 'Parceiro' : 'Legatus' }}
                </dd>
              </div>
            </dl>
          </section>
        </aside>
      </div>
    }

    <!-- ────────────────────────── linha do tempo ────────────────────────── -->
    @if (shownTab === 'timeline') {
      <div class="grid" role="tabpanel">
        <section class="card grid__main">
          @if (timeline()?.entries?.length) {
            <ol class="events">
              @for (
                entrada of timeline()!.entries;
                track entrada.occurredAt + entrada.eventType;
                let last = $last
              ) {
                <li class="events__item">
                  <div class="events__track">
                    <span
                      class="events__marker"
                      [class]="'events__marker--' + entrada.eventType"
                      aria-hidden="true">
                      <i [class]="'pi ' + iconeDoEvento(entrada)"></i>
                    </span>
                    @if (!last) {
                      <span class="events__line" aria-hidden="true"></span>
                    }
                  </div>

                  <div class="events__body">
                    <span class="events__title">{{ descreverEvento(entrada) }}</span>
                    @if (entrada.durationInStage) {
                      <span class="events__detail">
                        ficou {{ formatarDuracao(entrada.durationInStage) }} nesta etapa
                      </span>
                    }
                    @if (entrada.notes) {
                      <p class="events__notes">{{ entrada.notes }}</p>
                    }
                    <!-- Autor ao lado da data (card 3067): a pergunta da reunião de 25/09 foi
                         "foi aprovado pelo Rafa, pelo Guilherme?". O texto vem pronto do servidor,
                         que decide o que cada perfil pode ver. -->
                    <span class="events__when">
                      {{ entrada.occurredAt | vaultDateTime }}
                      @if (entrada.performedBy) {
                        <span class="events__author">
                          · <i class="pi pi-user" aria-hidden="true"></i> {{ entrada.performedBy }}
                        </span>
                      }
                    </span>
                  </div>
                </li>
              }
            </ol>
          } @else if (timelineFalhou()) {
            <!-- Falha de carga NÃO é histórico vazio (card 3189): dizer "sem histórico" aqui fazia
                 parecer que os registros do deal tinham sido apagados. -->
            <div class="deal-empty deal-empty--inline" role="alert">
              <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
              <strong>Não foi possível carregar a linha do tempo</strong>
              <span
                >O histórico do deal continua guardado. Recarregue a página para tentar de
                novo.</span
              >
            </div>
          } @else {
            <div class="deal-empty deal-empty--inline">
              <i class="pi pi-clock" aria-hidden="true"></i>
              <strong>Sem histórico ainda</strong>
              <span>
                O histórico passou a ser registrado em 23/09/2026 — deals anteriores começam vazios.
              </span>
            </div>
          }
        </section>

        @if (timeline()?.stageDurations?.length) {
          <aside class="grid__aside">
            <section class="card card--tight">
              <h3 class="card__title card__title--sm">Tempo por etapa</h3>
              <!--
                O total é a SOMA das passagens — o deal pode voltar de etapa. Por isso a contagem
                de visitas aparece: sem ela, um total alto numa etapa visitada duas vezes
                pareceria um deal travado, e não um que foi e voltou.
              -->
              <dl class="rows">
                @for (dur of timeline()!.stageDurations; track dur.stage) {
                  <div class="rows__row" [class.rows__row--current]="dur.isCurrent">
                    <dt>
                      {{ rotuloEtapa[dur.stage] }}
                      @if (dur.visits > 1) {
                        <span class="tag">{{ dur.visits }} passagens</span>
                      }
                    </dt>
                    <dd>{{ formatarDuracao(dur.total) }}</dd>
                  </div>
                }
              </dl>
            </section>
          </aside>
        }
      </div>
    }
  </div>
}

<!-- Comentários de um banco da Jornada nos bancos (card 3707): um painel só, aberto pela aba Bancos ou pelo cartão
     da Visão geral. -->
<app-bank-journey-comments-drawer
  [card]="bankJourney.selectedCard()"
  (closed)="bankJourney.closeComments()"
  (commented)="bankJourney.reload()" />

<!-- A modal serve aos dois momentos: primeiro envio e acréscimo posterior. -->
<app-modal-term-signatories #modalAssinantes (salvou)="termoAlterado()" />

<!-- Rejeitar e reabrir dividem a modal: a pergunta é a mesma — por quê? — e a resposta vai para o
     mesmo lugar, a linha do tempo que o parceiro lê. -->
<app-modal-deal-decision #modalDecisao (decidiu)="decisaoTomada()" />
<app-modal-committee-submit #modalCommitteeSubmit (submitted)="onCommitteeDealChanged($event)" />
